import FormSection from "../../../../common/components/functional/formSection/FormSection";
import { Button, Grid, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { DesktopDatePicker } from "@mui/x-date-pickers";
import {
  Controller,
  FieldValues,
  useFormContext,
  ValidateResult,
} from "react-hook-form";
import { format, isBefore, isSameDay, isValid } from "date-fns";
import isEmpty from "../../../../common/helpers/isEmpty";
import { Kind } from "../../../employment/employmentApi";
import {
  isoFormat,
  leadingZerosDateFormat,
} from "../../../../common/helpers/formatDateTime";

type Props = {
  isLoading: boolean;
  onSubmit: (fieldValues: FieldValues, kind: Kind) => void;
};

const ExpensesForEmploymentFormFields = ({ isLoading, onSubmit }: Props) => {
  const { t } = useTranslation("talentProfile");

  const validateDate = (value: Date): ValidateResult => {
    const isValidDate = !!value && isValid(value) && !isNaN(value.getTime());

    if (!isValidDate) {
      return t("timePeriodContainsInvalidDates");
    }

    const formatted = format(value, isoFormat);
    const parts = formatted.split("-");
    const yearContainsLeadingZeros = /^0+/.test(parts[0]);

    if (formatted.length !== 10 || yearContainsLeadingZeros) {
      return t("timePeriodContainsInvalidDates");
    }

    return true;
  };

  const validateDateTo = (
    value: Date,
    formValues: FieldValues,
  ): ValidateResult => {
    const dateResult = validateDate(value);
    if (dateResult !== true) return dateResult;

    const dateFrom = formValues.dateFrom;
    if (
      dateFrom &&
      isValid(dateFrom) &&
      !(isSameDay(dateFrom, value) || isBefore(dateFrom, value))
    ) {
      return t("startDateCannotBeBeforeEndDate");
    }

    return true;
  };

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useFormContext();

  const disabled = !isEmpty(errors);

  return (
    <FormSection title={t("expensesForEmployment")}>
      <Grid item xs={2} alignSelf="center" textAlign="end">
        <Typography variant="secondary">{t("common:timePeriod")}</Typography>
      </Grid>
      <Grid item container alignItems="center" columnGap={3} xs={10}>
        <Controller
          name="dateFrom"
          control={control}
          rules={{
            required: true,
            validate: validateDate,
          }}
          render={({ field }) => (
            <DesktopDatePicker
              {...field}
              format={leadingZerosDateFormat}
              label={null}
              slotProps={{
                textField: {
                  error: !!errors.dateFrom,
                  helperText: errors.dateFrom?.message?.toString(),
                  size: "small",
                  sx: { maxWidth: "160px" },
                },
              }}
            />
          )}
        />
        <>{t("invoices:separator")}</>
        <Controller
          name="dateTo"
          control={control}
          rules={{
            required: true,
            validate: (value, formValues) => validateDateTo(value, formValues),
          }}
          render={({ field }) => (
            <DesktopDatePicker
              {...field}
              format={leadingZerosDateFormat}
              label={null}
              slotProps={{
                textField: {
                  error: !!errors.dateTo,
                  helperText: errors.dateTo?.message?.toString(),
                  size: "small",
                  sx: { maxWidth: "160px" },
                },
              }}
            />
          )}
        />
        <Button
          onClick={handleSubmit((data) => onSubmit(data, Kind.Draft))}
          disabled={disabled || isLoading}
          sx={{ borderRadius: "20px" }}
          variant="outlined"
        >
          {t("searchDraft")}
        </Button>
        <Button
          onClick={handleSubmit((data) => onSubmit(data, Kind.Official))}
          disabled={disabled || isLoading}
          sx={{ borderRadius: "20px" }}
          variant="outlined"
        >
          {t("searchOfficial")}
        </Button>
      </Grid>
    </FormSection>
  );
};

export default ExpensesForEmploymentFormFields;
